import type { MeContextResponse } from '@saniplus/contracts';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
  GPS_TIMEOUT_MS,
  acknowledgeFailed,
  captureGps,
  enqueuePunch,
  nowIso,
  refreshContext,
  selectHomeState,
  selectQueueBanner,
  selectQueueState,
  useGetMeContextQuery,
  type ContextResult,
  type HomeState,
  type HomeView,
  type QueueBanner,
  type QueueState,
} from '../../offline';
import {
  deriveRecovery,
  type GpsGate,
  type RecoveryAttempt,
  type RecoveryView,
} from '../lib/conditions';
import { probeGpsPermission } from '../lib/gpsPermission';
import { useServerNow } from './useServerNow';

/**
 * The punch screen's whole orchestration: what is on screen, and what a tap
 * does.
 *
 * Everything factual comes from `features/offline` — that feature owns the
 * queue, the clock and the six home states, and this hook adds nothing to
 * them. What it owns is the small amount of *screen* state the engine
 * deliberately does not have: which recovery condition the worker is looking
 * at, whether the location permission is refused, and whether a tap is in
 * flight during the seconds `captureGps` may spend before a row exists.
 *
 * ## Two loaders, on purpose
 *
 * `useGetMeContextQuery()` is the live source. It is the **same cache entry**
 * the app shell subscribes to, so this costs no extra request, and the punch
 * mutations invalidate `Context`, so a confirmed punch refetches it on its own.
 *
 * `refreshContext()` runs beside it, on mount and whenever the network or the
 * foreground comes back. That thunk is the only thing that writes the
 * IndexedDB `contextCache` and the only thing that reads it back — without it
 * the offline screen is blank after the cold start iOS does routinely, which
 * is the exact scenario the whole offline engine exists for.
 */
export interface PunchScreen {
  /** No context from either source yet. */
  readonly loading: boolean;
  readonly context: MeContextResponse | null;
  /** True when the payload came off IndexedDB rather than the network. */
  readonly contextIsCached: boolean;
  readonly home: HomeView;
  readonly queue: QueueState;
  readonly banner: QueueBanner;
  /** The W8 screen to render instead of the home screen, or `null`. */
  readonly recovery: RecoveryView | null;
  /** "Now" on the server's clock, ticking every second while the tab is visible. */
  readonly now: number;
  /** A tap is in flight — including the GPS capture, before any row exists. */
  readonly busy: boolean;
  /**
   * The DEVICE clock at the moment of the last tap that the server confirmed.
   *
   * The queue row is deleted the instant the server answers, so by the time
   * the CONFIRMED screen renders there is nothing left to compare the server's
   * `recordedAt` against — and « Heure retenue » exists precisely to show that
   * comparison. This is the one fact the engine does not keep, so the screen
   * keeps it.
   */
  readonly confirmedDeviceAt: string | null;
  readonly punchIn: () => void;
  readonly punchOut: () => void;
  /** From the GPS screen: punch with the permission still refused (D-09). */
  readonly punchAnyway: () => void;
  /** Ask the browser for the location again, then continue. */
  readonly requestLocation: () => void;
  /** Leave the recovery screen; acknowledges the refusal when there is one. */
  readonly dismissRecovery: () => void;
  readonly reload: () => void;
}

const EMPTY_CONTEXT: ContextResult = { context: null, source: 'none', cachedAt: null };

/**
 * The two home states where nothing on screen moves: no elapsed time, no
 * countdown, only a date. `HomeScreen` is a big tree with a punch key in it,
 * and reconciling all of it once a second on a mid-range Android competes with
 * the tap that key exists for. Every other state counts something —
 * `WINDOW_NOT_OPEN_YET` included, which counts down to the minute the key
 * unlocks and flips its own sentence when the window shuts.
 */
const TICKLESS: ReadonlySet<HomeState> = new Set<HomeState>([
  'NO_SHIFT_TODAY',
  'BLOCKED_STALE_OPEN',
]);

export function shouldTick(state: HomeState): boolean {
  return !TICKLESS.has(state);
}

export function usePunchScreen(): PunchScreen {
  const dispatch = useAppDispatch();
  const queue = useAppSelector(selectQueueState);
  // Starts ticking: the state that decides is only known once `home` is
  // computed below, and a screen that counts must never be the one that waits.
  const [ticking, setTicking] = useState(true);
  const now = useServerNow(1_000, ticking);

  const query = useGetMeContextQuery();
  const [mirror, setMirror] = useState<ContextResult>(EMPTY_CONTEXT);
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState<RecoveryAttempt | null>(null);
  const [confirmedDeviceAt, setConfirmedDeviceAt] = useState<string | null>(null);
  const [gpsGate, setGpsGate] = useState<GpsGate | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const reload = useCallback((): void => {
    void dispatch(refreshContext())
      .unwrap()
      .then((next) => {
        if (mounted.current) setMirror(next);
      })
      .catch(() => undefined);
  }, [dispatch]);

  useEffect(() => {
    reload();
  }, [reload]);

  // The two moments a stale context is most misleading: the network came back,
  // and the worker brought the app to the foreground. iOS has no Background
  // Sync, so foreground is the trigger that matters (`01-PRODUCT-SPEC` §2.3).
  useEffect(() => {
    const onForeground = (): void => {
      if (document.visibilityState === 'visible') reload();
    };
    window.addEventListener('online', reload);
    document.addEventListener('visibilitychange', onForeground);
    return () => {
      window.removeEventListener('online', reload);
      document.removeEventListener('visibilitychange', onForeground);
    };
  }, [reload]);

  const live = query.data ?? null;
  const context = live ?? mirror.context;
  const contextIsCached = live === null && mirror.source === 'cache';

  const submit = useCallback(
    (type: 'in' | 'out', shiftId: string | null): void => {
      // D-08: an IN always names its shift. With no shift there is nothing to
      // punch into, and sending an empty id would be a 400 the worker cannot act on.
      if (type === 'in' && (shiftId === null || shiftId === '')) {
        setBusy(false);
        return;
      }
      const args = type === 'in' ? ({ type, shiftId: shiftId as string } as const) : ({ type } as const);

      setBusy(true);
      const attemptedAt = nowIso();
      // The device's own clock, which is what the engine writes into
      // `capturedAt.deviceRecordedAt` a millisecond later.
      const deviceAt = new Date().toISOString();
      void dispatch(enqueuePunch(args))
        .unwrap()
        .then((outcome) => {
          if (!mounted.current) return;
          setAttempt({
            clientPunchUuid: outcome.clientPunchUuid,
            type,
            outcome: outcome.outcome,
            refusalCode: outcome.refusalCode,
            attemptedAt,
          });
          // A confirmed punch has no recovery screen: the home screen's
          // CONFIRMED moment is the answer. The refresh keeps the IndexedDB
          // mirror in step with the session the server just opened.
          if (outcome.outcome === 'confirmed') {
            setConfirmedDeviceAt(deviceAt);
            reload();
          }
        })
        .catch(() => {
          if (!mounted.current) return;
          // `enqueuePunch` only rejects when IndexedDB itself refused the
          // write, which means nothing was recorded anywhere. Treating it as a
          // refusal is the honest reading: the worker must be told (I-7).
          setAttempt({
            clientPunchUuid: '',
            type,
            outcome: 'refused',
            refusalCode: 'SERVICE_UNAVAILABLE',
            attemptedAt,
          });
        })
        .finally(() => {
          if (mounted.current) setBusy(false);
        });
    },
    [dispatch, reload],
  );

  const start = useCallback(
    (type: 'in' | 'out'): void => {
      const shiftId = context?.currentShift?.id ?? null;
      setAttempt(null);
      setBusy(true);
      void probeGpsPermission()
        .then((permission) => {
          if (!mounted.current) return;
          if (permission === 'denied') {
            // Informational, never blocking: the screen's primary action is
            // « Pointer l'entrée quand même » and the punch goes either way.
            setGpsGate({ type, shiftId, attemptedAt: nowIso() });
            setBusy(false);
            return;
          }
          submit(type, shiftId);
        })
        .catch(() => {
          if (mounted.current) submit(type, shiftId);
        });
    },
    [context, submit],
  );

  const punchIn = useCallback((): void => {
    start('in');
  }, [start]);

  const punchOut = useCallback((): void => {
    start('out');
  }, [start]);

  const punchAnyway = useCallback((): void => {
    const gate = gpsGate;
    if (gate === null) return;
    setGpsGate(null);
    submit(gate.type, gate.shiftId);
  }, [gpsGate, submit]);

  const requestLocation = useCallback((): void => {
    // The Permissions API cannot re-prompt; only a real capture attempt can,
    // and this one is the same 8 s, never-blocking call the punch itself makes.
    void captureGps({ timeoutMs: GPS_TIMEOUT_MS }).then(() => {
      void probeGpsPermission().then((permission) => {
        if (mounted.current && permission !== 'denied') setGpsGate(null);
      });
    });
  }, []);

  const recovery = deriveRecovery({ attempt, gpsGate, failures: queue.failed });

  const dismissRecovery = useCallback((): void => {
    const uuid = recovery?.acknowledges ?? null;
    if (uuid !== null && uuid !== '') void dispatch(acknowledgeFailed(uuid));
    setAttempt(null);
    setGpsGate(null);
  }, [dispatch, recovery]);

  const home = selectHomeState(context, queue, now, { contextIsCached });
  const tick = shouldTick(home.state);
  useEffect(() => {
    setTicking(tick);
  }, [tick]);

  return {
    loading: context === null && query.isLoading,
    context,
    contextIsCached,
    home,
    queue,
    banner: selectQueueBanner(queue),
    recovery,
    now,
    busy,
    confirmedDeviceAt,
    punchIn,
    punchOut,
    punchAnyway,
    requestLocation,
    dismissRecovery,
    reload,
  };
}
